import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { toggleSidebarMenu, setSidebarMenuToggle, setMobileSidebarOpen } from '@app/store/reducers/ui';
import { useAuth } from '../../../contexts/AuthContext';
import i18n from '@app/utils/i18n';

export interface IMenuItem {
  name: string;
  icon?: string;
  path?: string;
  shortcut?: string;
  permissionKey?: string;
  children?: Array<IMenuItem>;
}

export const MENU: IMenuItem[] = [
  {
    name: i18n.t('menusidebar.label.dashboard'),
    icon: 'bx bx-home-alt',
    path: '/',
    permissionKey: 'dashboard',
  },
  {
    name: 'User',
    icon: 'bx bx-user-circle',
    children: [
      { name: 'View Users',     icon: 'bx bx-group',        path: '/users',     permissionKey: 'users' },
      { name: 'View User Type', icon: 'bx bx-id-card',      path: '/user-type'                         },
    ],
  },
  {
    name: 'Doctors',
    icon: 'bx bx-plus-medical',
    path: '/doctors',
    permissionKey: 'doctor',
  },
  {
    name: 'Patients',
    icon: 'bx bx-user-pin',
    path: '/patients',
    permissionKey: 'patients',
  },
  {
    name: 'Reports',
    icon: 'bx bx-file-blank',
    path: '/patients-reports',
    permissionKey: 'report',
  },
  {
    name: 'Test Settings',
    icon: 'bx bx-cog',
    permissionKey: 'test_settings',
    children: [
      { name: 'Department',           icon: 'bx bx-building',     path: '/departments'       },
      { name: 'Test Category',        icon: 'bx bx-category',     path: '/testcategories'    },
      { name: 'Test',                 icon: 'bx bx-test-tube',  path: '/test-types'        },
      { name: 'Profile',              icon: 'bx bx-user-check',   path: '/test-profile'      },
      { name: 'Terms and Conditions', icon: 'bx bx-detail',     path: '/terms-conditions'  },
    ],
  },
  {
    name: 'Billing',
    icon: 'bx bx-receipt',
    path: '/billing',
    permissionKey: 'billing',
  },
  {
    name: 'Payments',
    icon: 'bx bx-credit-card',
    path: '/payments',
    permissionKey: 'payments',
  },
  {
    name: 'Finance',
    icon: 'bx bx-line-chart',
    permissionKey: 'finance',
    children: [
      { name: 'Income',         icon: 'bx bx-trending-up',  path: '/income-by-reference', permissionKey: 'income'         },
      { name: 'Expense',        icon: 'bx bx-trending-down', path: '/expenses',            permissionKey: 'expense'        },
      { name: 'Finance Report', icon: 'bx bx-pie-chart-alt', path: '/finance-report',      permissionKey: 'finance_report' },
    ],
  },
  {
    name: 'Backup',
    icon: 'bx bx-cloud-download',
    path: '/backup',
  },
  {
    name: 'Login User',
    icon: 'bx bx-user-circle',
    children: [
      { name: 'Profile',         icon: 'bx bx-user',         path: '/profile'         },
      { name: 'Change Password', icon: 'bx bx-lock-alt',     path: '/change-password' },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Permission filter helper
// ─────────────────────────────────────────────────────────────────────────────

function filterMenu(
  items: IMenuItem[],
  permissions: Record<string, boolean>
): IMenuItem[] {
  return items.reduce<IMenuItem[]>((acc, item) => {
    // Skip if user doesn't have the required permission
    if (item.permissionKey && !permissions[item.permissionKey]) {
      return acc;
    }

    if (item.children?.length) {
      const visibleChildren = filterMenu(item.children, permissions);
      // Hide parent if no children are visible
      if (visibleChildren.length === 0) return acc;
      acc.push({ ...item, children: visibleChildren });
    } else {
      acc.push(item);
    }

    return acc;
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// SidebarMenuItem
// ─────────────────────────────────────────────────────────────────────────────

const SidebarMenuItem = ({ item }: { item: IMenuItem }) => {
  const location      = useLocation();
  const dispatch      = useAppDispatch();
  const hasChildren   = Boolean(item.children?.length);
  const isActive      = item.path ? location.pathname === item.path : false;
  const isChildActive = hasChildren
    ? item.children!.some((c) => c.path && location.pathname.startsWith(c.path))
    : false;

  const handleLinkClick = () => {
    if (window.innerWidth < 1200) {
      dispatch(setMobileSidebarOpen(false));
    }
  };

  if (hasChildren) {
    return (
      <li className={isChildActive ? 'mm-active' : ''}>

        <a className={`has-arrow ${isChildActive ? 'active' : ''}`}
          href="javascript:;"
          aria-expanded={isChildActive ? 'true' : 'false'}
        >
          <div className="parent-icon"><i className={item.icon} /></div>
          <div className="menu-title">{item.name}</div>
        </a>
        <ul className={`mm-collapse ${isChildActive ? 'mm-show' : ''}`}>
          {item.children!.map((child) => (
            <li
              key={child.name + child.path}
              className={location.pathname === child.path ? 'mm-active' : ''}
            >
              <Link
                to={child.path || '/'}
                className={location.pathname === child.path ? 'active' : ''}
                onClick={handleLinkClick}
              >
                <i className={child.icon} />
                <span className="menu-title">{child.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className={isActive ? 'mm-active' : ''}>
      <Link to={item.path || '/'} className={isActive ? 'active' : ''} onClick={handleLinkClick}>
        <div className="parent-icon"><i className={item.icon} /></div>
        <div className="menu-title">{item.name}</div>
      </Link>
    </li>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MenuSidebar
// ─────────────────────────────────────────────────────────────────────────────

const MenuSidebar = () => {
  const menuRef            = useRef<HTMLUListElement>(null);
  const navRef             = useRef<HTMLDivElement>(null);
  const dispatch           = useAppDispatch();
  const menuSidebarCollapsed = useAppSelector((state) => state.ui.menuSidebarCollapsed);

  const { user }    = useAuth();
  const permissions = (user?.permissions || {}) as Record<string, boolean>;
  const visibleMenu = filterMenu(MENU, permissions);

  // ── MetisMenu ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).jQuery && menuRef.current) {
      const $ = (window as any).jQuery;
      $(menuRef.current).metisMenu();
    }
  }, []);

  // ── SimpleBar ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (navRef.current && (window as any).SimpleBar) {
      new (window as any).SimpleBar(navRef.current, { autoHide: true });
    }
  }, []);

  // ── Hover expand / collapse ───────────────────────────────────────────────
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar-wrapper') as HTMLElement | null;
    if (!sidebar) return;

    const onEnter = () => {
      if (window.innerWidth >= 1200) {
        document.querySelectorAll('.wrapper').forEach((w) => w.classList.add('sidebar-hovered'));
        document.body.classList.add('sidebar-hovered');
      }
    };
    const onLeave = () => {
      document.querySelectorAll('.wrapper').forEach((w) => w.classList.remove('sidebar-hovered'));
      document.body.classList.remove('sidebar-hovered');
    };

    sidebar.addEventListener('mouseenter', onEnter);
    sidebar.addEventListener('mouseleave', onLeave);
    return () => {
      sidebar.removeEventListener('mouseenter', onEnter);
      sidebar.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  const handleToggleSidebar = () => {
    if (window.innerWidth < 1200) {
      dispatch(setMobileSidebarOpen(false));
    } else {
      dispatch(toggleSidebarMenu());
    }
  };

  return (
    <div className="sidebar-wrapper">
      {/* ── Logo / arrow row ─────────────────────────────────────────────── */}
      <div className="sidebar-header">
        <div>
          <img src="/assets/images/logo-icon.png" className="logo-icon" alt="logo icon" />
        </div>
        <div>
          <h4 className="logo-text">EZY Lab</h4>
        </div>
        <div
          className="toggle-icon ms-auto"
          onClick={handleToggleSidebar}
          style={{ cursor: 'pointer' }}
        >
          <i className="bx bx-arrow-back" />
        </div>
      </div>

      {/* ── Scrollable nav ───────────────────────────────────────────────── */}
      <div
        ref={navRef}
        style={{ height: 'calc(100vh - 60px)', overflowY: 'auto', overflowX: 'hidden' }}
      >
        <ul className="metismenu" id="menu" ref={menuRef}>
          {visibleMenu.map((item) => (
            <SidebarMenuItem key={item.name + (item.path || '')} item={item} />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default MenuSidebar;